import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useToast } from '../components/Toast';

export interface SlaPolicy {
  id: string;
  name: string;
  category?: string | null;
  priority?: string | null;
  responseHours?: number | null;
  resolutionHours: number;
  businessHoursOnly: boolean;
  isDefault: boolean;
  isActive: boolean;
  priorityOrder: number;
}

export function useSlaPolicies() {
  const { toast } = useToast();
  const [policies, setPolicies] = useState<SlaPolicy[]>([]);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/sla-policies');
      setPolicies(res.data.policies ?? []);
    } catch (e: any) {
      toast(e?.response?.data?.message || 'Failed to load SLA policies', 'error');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { refetch(); }, [refetch]);

  const create = async (data: Partial<SlaPolicy>) => {
    try { await api.post('/admin/sla-policies', data); toast('SLA policy created', 'success'); await refetch(); return true; }
    catch (e: any) { toast(e?.response?.data?.message || 'Create failed', 'error'); return false; }
  };
  const update = async (id: string, data: Partial<SlaPolicy>) => {
    try { await api.put(`/admin/sla-policies/${id}`, data); toast('SLA policy updated', 'success'); await refetch(); return true; }
    catch (e: any) { toast(e?.response?.data?.message || 'Update failed', 'error'); return false; }
  };
  const remove = async (id: string) => {
    try { await api.delete(`/admin/sla-policies/${id}`); toast('SLA policy removed', 'success'); await refetch(); }
    catch (e: any) { toast(e?.response?.data?.message || 'Delete failed', 'error'); }
  };

  return { policies, loading, refetch, create, update, remove };
}
